import React from 'react';
import { Alert, Stack } from '@mui/material';
import { SpreadSheetRow } from '@retail/products/types';
import { BaseCampaignImportItem, CampaignItemsImportConfig } from './types';
import { UploadContainer, useImportItemsStore } from '@retail/products/components';
import { ImportStateValidation } from '@retail/components';
import { useAppTFunction } from '@retail/app/i18n';

interface Props<T extends BaseCampaignImportItem> {
  onRemoveFile: () => void;
  onImportSuccess: (importedItems: T[]) => void;
  onImportError: () => void;
  importConfig: CampaignItemsImportConfig<T>;
}

export const UploadCampaignItems = <T extends BaseCampaignImportItem>({
  onRemoveFile,
  onImportSuccess,
  onImportError,
  importConfig,
}: Props<T>) => {
  const { importSpreadsheet, clearState: clearImportState, run } = useImportItemsStore<T>();
  const t = useAppTFunction();

  const handleImportFile = (inputRows: SpreadSheetRow[], file: File) => {
    const { rows, summary } = importSpreadsheet(inputRows, file, importConfig);

    if (summary.errorCount === 0 && rows.length > 0) {
      onImportSuccess(rows.filter((row) => row.status === 'ok').map((row) => row.values));
    } else {
      onImportError();
    }
  };

  return (
    <Stack gap={2} alignItems="center">
      <UploadContainer
        onDownloadTemplate={() => importConfig.downloadTemplateAsExcel('campaignItemsTemplate')}
        importSpreadsheet={handleImportFile}
        resetImportedStates={clearImportState}
      />
      {run?.summary.status === 'success' && (
        <Alert severity="success" sx={{ display: 'flex', alignItems: 'center' }}>
          {t('products.uploadItems.nrItemsRead', {
            count: run.summary.okCount,
            totalCount: run.summary.totalCount,
          })}
        </Alert>
      )}

      {run && (
        <ImportStateValidation
          missingRequiredColumns={run.missingRequiredColumns}
          noRows={run.rows.length === 0}
          invalidDataRows={run.rows
            .filter((row) => row.status === 'error')
            .map((row) => ({
              index: row.rowIndex,
              errors: row.errors
                .map((error) => ({
                  column: error.column,
                  reason: error.reason,
                  value: error.raw,
                }))
                .concat(
                  // @ts-ignore
                  (row.rowErrors ?? []).map(({ message }) => ({
                    reason: message,
                  }))
                ),
            }))}
        />
      )}
    </Stack>
  );
};
